/** Temporary browser-only bytes. Failed writes/deletes are reported to the conversion UI;
 * a failed cleanup is not described as harmless and must not imply successful retention cleanup. */
const DB_NAME = 'profenz_fenzsafe_guest_documents';
const STORE_NAME = 'blobs';
const DB_VERSION = 1;

type StoredBlob = {type: string; buffer: ArrayBuffer};

/** FileReader, not Blob.prototype.arrayBuffer() -- broadly supported for far longer, and the one that this repo's own test environment (jsdom) actually implements for a Blob built from an in-memory File. */
function readBlobAsArrayBuffer(blob: Blob): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error ?? new Error('Could not read the document.'));
    reader.readAsArrayBuffer(blob);
  });
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB is not available in this browser.'));
      return;
    }
    let request: IDBOpenDBRequest;
    try {
      request = indexedDB.open(DB_NAME, DB_VERSION);
    } catch (thrown) {
      reject(thrown instanceof Error ? thrown : new Error('Could not open guest document storage.'));
      return;
    }
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE_NAME)) {
        request.result.createObjectStore(STORE_NAME);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('Could not open guest document storage.'));
    request.onblocked = () => reject(new Error('Guest document storage is blocked by another open tab.'));
  });
}

/** Saves one document's real bytes under `key`, overwriting any prior value at that key. */
export async function saveGuestDocumentBlob(key: string, blob: Blob): Promise<void> {
  try {
    const buffer = await readBlobAsArrayBuffer(blob);
    const record: StoredBlob = {type: blob.type, buffer};
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).put(record, key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error ?? new Error('Could not save the document for later.'));
      tx.onabort = () => reject(tx.error ?? new Error('Could not save the document for later.'));
    });
    db.close();
  } catch {
    throw new Error('This browser could not save the guest documents. Keep this tab open and try again.');
  }
}

/** Returns the Blob saved under `key`, or null if it was never saved, already claimed/deleted, or storage is unavailable. */
export async function loadGuestDocumentBlob(key: string): Promise<Blob | null> {
  try {
    const db = await openDb();
    const result = await new Promise<StoredBlob | null>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const req = tx.objectStore(STORE_NAME).get(key);
      req.onsuccess = () => resolve((req.result as StoredBlob | undefined) ?? null);
      req.onerror = () => reject(req.error ?? new Error('Could not load the saved document.'));
    });
    db.close();
    return result ? new Blob([result.buffer], {type: result.type}) : null;
  } catch {
    return null;
  }
}

/** Removes the named temporary documents; a failure must be reported to the caller. */
export async function deleteGuestDocumentBlobs(keys: string[]): Promise<void> {
  if (keys.length === 0) return;
  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      for (const key of keys) store.delete(key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error ?? new Error('Could not clean up saved documents.'));
      tx.onabort = () => reject(tx.error ?? new Error('Could not clean up saved documents.'));
    });
    db.close();
  } catch {
    throw new Error('Temporary guest documents could not be removed from this browser.');
  }
}

/** One pending guest envelope owns this database. Remove expired/replaced/orphaned bytes. */
export async function pruneGuestDocumentBlobs(keepKeys: string[]): Promise<void> {
  const db = await openDb();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const keep = new Set(keepKeys);
      const cursor = tx.objectStore(STORE_NAME).openCursor();
      cursor.onsuccess = () => {
        const row = cursor.result;
        if (!row) return;
        if (!keep.has(String(row.key))) row.delete();
        row.continue();
      };
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error ?? new Error('Guest document cleanup failed.'));
      tx.onabort = () => reject(tx.error ?? new Error('Guest document cleanup failed.'));
    });
  } finally { db.close(); }
}
